import React, { useEffect, useState } from "react";
import { config } from "../../data/config";
import { Button, Message, Progress, Loader } from "rsuite";
import axios from "axios";
import { getCookies } from "cookies-next";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import "rsuite/dist/rsuite.min.css"; // Ensure RSuite styles are loaded

const CHUNK_SIZE = 512 * 1024; // ~0.5 MB per chunk

function buildMediaUrl(base: string, urlPath: string): string {
  if (!urlPath) return "";
  if (/^https?:\/\//i.test(urlPath)) return urlPath;
  const normalized = urlPath.startsWith("/") ? urlPath : `/${urlPath}`;
  const encoded = normalized
    .split("/")
    .map((segment) => (segment ? encodeURIComponent(segment) : ""))
    .join("/");
  return `${base.replace(/\/$/, "")}${encoded}`;
}

interface MyComponentProps {
  component: string;
  id: string;
}

const ChunkedFileUpload: React.FC<MyComponentProps> = ({ component, id }) => {
  const cookie = getCookies();
  const { t } = useTranslation();

  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState<boolean>(true);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setError(null); // Clear previous errors
  };

  const loadimage = async () => {
    if (!id || id === "new" || id === "undefined") {
      setData(null);
      setLoadingPreview(false);
      return;
    }
    setLoadingPreview(true);
    try {
      const response = await axios.get(`${config.url}/v1/dashboard/cunkupload/${id}`, {
        params: { component },
        headers: {
          Authorization: "Bearer " + cookie["token"],
        },
      });
      const file = response.data.message;
      setData(file && file.component === component ? file : null);
    } catch (err) {
      console.error("Failed to load file:", err);
      setData(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const deletedItem = async () => {
    const result = await Swal.fire({
      title: t("Are you sure?"),
      text: t("Do you want to delete this file?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("Yes, delete it!"),
      cancelButtonText: t("Cancel"),
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${config.url}/v1/dashboard/cunkupload/${id}`, {
        params: { component },
        headers: {
          Authorization: "Bearer " + cookie["token"],
        },
      });
      setData(null);
      setFile(null);
      Swal.fire({
        icon: "success",
        title: t("File deleted successfully"),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("Error deleting file"),
      });
      console.error(err);
    }
  };

  const uploadFile = async () => {
    if (!file) return;
    if (!id || id === "new" || id === "undefined") {
      setError(t("Save the property first, then upload a video."));
      return;
    }

    setUploading(true);
    setError(null);
    setProgress(0);

    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uniqueFileId = `${file.name}-${Date.now()}`;

    try {
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append("file", chunk);
        formData.append("filename", uniqueFileId);
        formData.append("chunkIndex", chunkIndex.toString());
        formData.append("totalChunks", totalChunks.toString());
        formData.append("originalFileName", file.name);
        formData.append("component", component);
        formData.append("id", id);

        const response = await fetch(`${config.url}/v1/dashboard/cunkupload`, {
          method: "POST",
          body: formData,
          headers: {
            Authorization: "Bearer " + cookie["token"],
          },
        });

        if (!response.ok) {
          const body = await response.text().catch(() => "");
          throw new Error(body || `Upload failed (${response.status})`);
        }

        setProgress(((chunkIndex + 1) / totalChunks) * 100);
      }

      setFile(null);
      await loadimage();
      Swal.fire({
        icon: "success",
        title: t("Uploaded successfully"),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Error uploading chunk:", err);
      setError(t("Upload failed. Please try again."));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    loadimage();
  }, [id, component]);

  return (
    <div
      dir="rtl"
      className="p-5 border rounded-lg shadow-md w-full max-w-md mx-auto text-center bg-white"
    >
      <h2 className="text-xl font-bold mb-4 text-gray-800">{t("Upload video")}</h2>

      {(!id || id === "new" || id === "undefined") && (
        <Message type="warning" className="mb-4">
          {t("Save the property first, then upload a video.")}
        </Message>
      )}

      {/* Preview */}
      {loadingPreview ? (
        <div className="py-4">
          <Loader size="sm" />
        </div>
      ) : data ? (
        <div className="mb-4">
          <video
            controls
            src={buildMediaUrl(config.fronturl, data.url)}
            className="w-full rounded"
            onError={() => setError(t("Could not load video preview."))}
          />
        </div>
      ) : (
        <p className="text-gray-500 mb-4">{t("No video uploaded yet.")}</p>
      )}

      {/* File Input */}
      <input
        type="file"
        accept="video/*,.mp4,.webm,.mov,.m4v"
        onChange={handleFileChange}
        className="w-full mb-4 text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        disabled={uploading || !id || id === "new" || id === "undefined"}
      />

      {/* Upload Button */}
      <Button
        appearance="primary"
        onClick={uploadFile}
        loading={uploading}
        disabled={!file || uploading || !id || id === "new" || id === "undefined"}
        block
        className="mb-4"
      >
        {uploading ? t("Uploading...") : t("Upload file")}
      </Button>

      {/* Progress Bar */}
      {uploading && (
        <div className="mb-3">
          <Progress.Line percent={progress} strokeColor="#3b82f6" />
          <p className="text-sm text-gray-600 mt-1">{Math.round(progress)}% {t("uploaded")}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <Message type="error" className="mb-3" closable onClose={() => setError(null)}>
          {error}
        </Message>
      )}

      {/* Delete Button */}
      {data && (
        <Button
          color="red"
          onClick={deletedItem}
          appearance="ghost"
          disabled={uploading}
          block
        >
          {t("Delete file")}
        </Button>
      )}
    </div>
  );
};

export default ChunkedFileUpload;